import { cn } from "@/lib/utils";
import { useStatusConfigs } from "@/hooks/use-shared-queries";
import type { StatusConfig } from "@shared/schema";

interface StatusBadgeProps {
  status: string;
  type?: 'project' | 'task' | 'pulse';
  className?: string;
}

export function StatusBadge({ status, type = 'task', className }: StatusBadgeProps) {
  // Use shared query hook for status configurations
  const { data: statusConfigs = [] } = useStatusConfigs();

  // Handle undefined/null status - treat as empty
  const safeStatus = status || 'empty';

  const getStatusColor = (status: string, type: string) => {
    // Find the status config for this status and type
    const config = statusConfigs.find(
      c => c.category === type && (
        c.displayName === status ||
        c.statusKey === status ||
        (status === 'empty' && c.displayName === '') ||
        (status === 'none' && c.displayName === '')
      )
    );
    
    // If we have a custom color from the database, use it
    if (config?.color) {
      return undefined; // We'll use inline style instead of class
    }
    
    // Fallback to default classes for backwards compatibility
    if (type === 'project') {
      switch (status.toLowerCase()) {
        case 'in production':
          return 'status-production';
        case 'final':
          return 'status-done';
        case 'on hold':
        case 'stuck - dependency':
        case 'stuck - feedback':
        case 'stuck - briefing':
        case 'stuck - abnahme':
          return 'status-stuck';
        case 'canceled':
          return 'status-canceled';
        case 'feedback available':
        case 'go':
          return 'status-feedback';
        case 'none':
        case 'empty':
        case '':
          return 'status-empty';
        default:
          return 'status-working';
      }
    } else if (type === 'pulse') {
      switch (status.toLowerCase()) {
        case 'done':
        case 'completed':
          return 'status-done';
        case 'working on it':
        case 'in progress':
          return 'status-working';
        case 'new message':
        case 'waiting':
          return 'status-stuck';
        case 'empty':
          return 'status-empty';
        default:
          return 'status-working';
      }
    } else {
      switch (status.toLowerCase()) {
        case 'go':
          return 'status-go';  // Dark grey for GO status
        case 'done':
          return 'status-done';
        case 'working on it':
          return 'status-working';
        case 'new message':
        case 'stuck':
          return 'status-stuck';
        case 'empty':
          return 'status-empty';
        default:
          return 'status-working';
      }
    }
  };

  const colorClass = getStatusColor(safeStatus, type);
  
  // Get custom color from database
  const config = statusConfigs.find(
    c => c.category === type && (
      c.displayName === safeStatus ||
      c.statusKey === safeStatus ||
      (safeStatus === 'empty' && c.displayName === '') ||
      (safeStatus === 'none' && c.displayName === '')
    )
  );

  // Handle empty status - display just the color with no text
  if (safeStatus === 'empty' || safeStatus === 'none' || config?.displayName === '') {
    return (
      <span
        className={cn(
          "status-badge",
          colorClass,
          type === 'project' ? 'project-status' : '',
          className
        )}
        style={config?.color ? { backgroundColor: config.color } : undefined}
      >
        &nbsp;
      </span>
    );
  }

  // Display the displayName if we have a config, otherwise show the status as-is
  const displayText = config?.displayName || safeStatus;
  
  return (
    <span
      className={cn(
        "status-badge",
        colorClass,
        type === 'project' ? 'project-status' : '',
        className
      )}
      style={config?.color ? { backgroundColor: config.color } : undefined}
    >
      {displayText}
    </span>
  );
}
